
import { useState, useEffect } from "react";
import { getFunnelBySubdomain } from "@/lib/db";
import { defaultFunnelData } from "@/components/FunnelBuilder/constants";
import type { FunnelData } from "@/components/FunnelBuilder/types";

const normalizeSlug = (text: string): string => {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Remove diacritics
    .replace(/[^a-z0-9]+/g, '-')     // Replace non-alphanumeric with hyphens
    .replace(/^-+|-+$/g, '');        // Remove leading/trailing hyphens
};

export const useFunnelData = (subdomain: string | undefined, currentPathSlug: string | undefined) => {
  const [funnel, setFunnel] = useState<FunnelData>(defaultFunnelData);
  const [isLoading, setIsLoading] = useState(true);
  const [currentSlug, setCurrentSlug] = useState<string>("");

  useEffect(() => {
    const loadFunnel = async () => {
      if (!subdomain) return;
      try {
        setIsLoading(true);
        const data = await getFunnelBySubdomain(subdomain);
        if (data) {
          // Normalize all page slugs
          if (data.pages) {
            data.pages = data.pages.map(page => ({
              ...page,
              slug: page.isMainPage ? "" : normalizeSlug(page.title)
            }));
          }

          const funnelData = { ...defaultFunnelData, ...data } as FunnelData;
          setFunnel(funnelData);
          console.log("Loaded funnel:", funnelData);
          
          // If no currentPathSlug, find the main page
          if (!currentPathSlug) {
            const mainPage = funnelData.pages.find(page => page.isMainPage);
            if (mainPage) {
              setCurrentSlug("");
              return;
            }
          }

          // Check if the current path slug exists in the pages
          const pageExists = currentPathSlug && funnelData.pages.some(page => 
            page.slug === currentPathSlug || 
            (page.isMainPage && currentPathSlug === "")
          );
          
          if (pageExists) {
            console.log("Setting page from URL slug:", currentPathSlug);
            setCurrentSlug(currentPathSlug);
          } else {
            // If no valid URL slug, find the main page or first page
            const mainPage = funnelData.pages.find(page => page.isMainPage);
            const firstPage = funnelData.pages[0];
            const defaultPage = mainPage || firstPage;
            
            if (defaultPage) {
              console.log("Setting default page:", defaultPage.slug);
              setCurrentSlug(defaultPage.isMainPage ? "" : defaultPage.slug);
            } else {
              console.warn("No valid pages found in funnel");
            }
          }
        }
      } catch (error) {
        console.error("Error loading funnel:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadFunnel();
  }, [subdomain, currentPathSlug]);

  return { funnel, isLoading, currentSlug, setCurrentSlug };
};
